import React, { useRef, useState } from 'react';
import { Participant } from '../types';
import { authenticatedFetch } from '../libraries/auth';
import { blockParticipant, useAppDispatch } from '../libraries/AppContext';
import { getFriendlyErrorMessage } from '../libraries/friendlyErrors';
import FloatingMessageBox from './FloatingMessageBox';

interface ParticipantDetailPageProps {
  participant: Participant;
  onEdit: (participantId: number) => void;
  currentUserId: number;
  onProfileImageUpdate?: () => void;
  onChat: (participantId: number) => Promise<boolean>;
  isStartingChat?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const ParticipantDetailPage: React.FC<ParticipantDetailPageProps> = ({
  participant,
  onEdit,
  currentUserId,
  onProfileImageUpdate
}) => {
  const dispatch = useAppDispatch();
  const fullName = `${participant.titel ? participant.titel + ' ' : ''}${participant.vorname} ${participant.nachname}`;
  const isOwnProfile = participant.userId === currentUserId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState<string | null>(null);

  const handleImageClick = () => {
    if (isOwnProfile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Bitte wählen Sie eine Bilddatei');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Das Bild muss kleiner als 5 MB sein');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await authenticatedFetch(`${API_URL}/api/users/${participant.userId}/profile-image`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Hochladen des Bildes fehlgeschlagen');
      }

      // Call the callback to reload event data
      if (onProfileImageUpdate) {
        onProfileImageUpdate();
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error instanceof Error ? error.message : 'Hochladen des Bildes fehlgeschlagen');
    } finally {
      setUploading(false);
      // Reset the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Simple sanitizer to remove script tags and on* attributes
  const sanitizeHTML = (html: string) => {
    if (typeof DOMParser === 'undefined') {
      return html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('script, style').forEach(el => el.remove());

    doc.querySelectorAll('*').forEach(el => {
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      }
    });

    return doc.body.innerHTML;
  };

  const safeAbout = participant.about ? sanitizeHTML(participant.about) : '';

  const handleBlockParticipant = async () => {
    if (isOwnProfile || participant.isBlocked || blocking) {
      return;
    }

    try {
      setBlocking(true);
      setBlockError(null);

      const response = await authenticatedFetch(`${API_URL}/api/participant/block`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({blockedParticipantId: participant.id})
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || 'Blockieren fehlgeschlagen');
      }

      blockParticipant(dispatch, participant.id, participant.userId);
    } catch (error) {
      console.error('Error blocking participant:', error);
      setBlockError(getFriendlyErrorMessage(error, {
        genericMessage: 'Der Teilnehmer konnte gerade nicht blockiert werden. Bitte versuchen Sie es erneut.',
        participantMessage: 'Der Teilnehmer konnte gerade nicht blockiert werden. Bitte versuchen Sie es später erneut.',
      }));
    } finally {
      setBlocking(false);
    }
  };

  return (
    <div className="space-y-4 p-4 bg-gray-50 rounded-lg shadow-2xl border-1 border-gray-200">
      {blockError && (
        <FloatingMessageBox
          title="Fehler"
          message={blockError}
          type="error"
          onClose={() => setBlockError(null)}
        />
      )}

      {/* Edit Button - Only show for own profile */}
      {isOwnProfile && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <button
            onClick={() => onEdit(participant.id)}
            className="w-full md:w-auto md:px-8 bg-pink-600 text-white py-3 rounded-lg font-medium hover:bg-pink-700 transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Profil bearbeiten
          </button>
        </div>
      )}

      {!isOwnProfile && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <button
            onClick={handleBlockParticipant}
            disabled={participant.isBlocked || blocking}
            className="w-full md:w-auto md:px-8 bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <span className="material-symbols-outlined text-xl leading-none mr-2">block</span>
            {participant.isBlocked ? 'Nutzer blockiert' : (blocking ? 'Wird blockiert...' : 'Nutzer blockieren')}
          </button>
        </div>
      )}

      {/* Profile Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
        <div className="flex flex-col items-center">
          {/* Profile Picture */}
          <div className="relative mb-4">
            {participant.profileImage ? (
              <img
                src={participant.profileImage}
                alt={fullName}
                className={`w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-1 border-primary ${uploading ? 'opacity-50' : ''}`}
              />
            ) : (
              <div
                className={`w-32 h-32 md:w-40 md:h-40 rounded-full border-1 border-primary bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-4xl md:text-5xl font-bold ${uploading ? 'opacity-50' : ''}`}
              >
                {participant.vorname.charAt(0)}{participant.nachname.charAt(0)}
              </div>
            )}

            {/* Edit Button - Only show for own profile */}
            {isOwnProfile && !uploading && (
              <>
                <button
                  onClick={handleImageClick}
                  className="absolute bottom-0 right-0 bg-pink-600 hover:bg-pink-700 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg transition-colors"
                  aria-label="Profilbild ändern"
                >
                  <span className="material-symbols-outlined text-xl leading-none">
                    photo_camera
                  </span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </>
            )}

            {/* Uploading Spinner */}
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
              </div>
            )}
          </div>

          {/* Upload Error Message */}
          {uploadError && (
            <div className="mb-2 text-red-600 text-sm text-center">
              {uploadError}
            </div>
          )}

          {/* Name */}
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 text-center mb-1">
            {fullName}
          </h1>

          {/* Position */}
          {participant.position && (
            <p className="text-base md:text-lg text-gray-600 text-center mb-2">
              {participant.position}
            </p>
          )}

          {/* Location */}
          <div className="flex items-center text-gray-600 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="font-medium text-base md:text-lg">{participant.standort}</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* About Section */}
        {safeAbout && safeAbout.trim() !== '' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Über
            </h2>
            <div
              className="prose prose-sm md:prose-base max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-a:text-pink-600 prose-strong:text-gray-700"
              dangerouslySetInnerHTML={{ __html: safeAbout }}
            />
          </div>
        )}

        {/* Contact Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Kontakt
          </h2>

          <div className="space-y-3">
            {/* Email */}
            <div className="flex items-start">
              <div className="w-8 flex-shrink-0">
                <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">E-Mail</p>
                <a
                  href={`mailto:${participant.email}`}
                  className="text-pink-600 hover:text-pink-700 break-all"
                >
                  {participant.email}
                </a>
              </div>
            </div>

            {/* Phone */}
            {participant.telefonnummer && (
              <div className="flex items-start">
                <div className="w-8 flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500 mb-1">Telefon</p>
                  <a
                    href={`tel:${participant.telefonnummer}`}
                    className="text-pink-600 hover:text-pink-700"
                  >
                    {participant.telefonnummer}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantDetailPage;
